import { useCallback, useEffect, useRef, useState } from 'react';
import { EventCard } from './components/EventCard';
import { fetchEvents, setTransport } from './api';
import { formatFullDate, formatTime } from './format';
import type { EventsResponse } from './types';

type Status = 'loading' | 'ready' | 'error';

/** Klíč rozpracované změny, aby šlo přepínat víc lidí naráz. */
const pendingKey = (eventId: string, userId: string) => `${eventId}:${userId}`;

export default function App() {
  const [data, setData] = useState<EventsResponse | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const lastLoadedAt = useRef(0);

  const load = useCallback(async (options: { refresh?: boolean } = {}) => {
    if (options.refresh) setRefreshing(true);
    try {
      const response = await fetchEvents(options);
      setData(response);
      setStatus('ready');
      setError(null);
      lastLoadedAt.current = Date.now();
    } catch (loadError) {
      // Když už nějaká data máme, nemažeme je kvůli výpadku sítě.
      setStatus((current) => (current === 'ready' ? 'ready' : 'error'));
      setError((loadError as Error).message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Data upravují i ostatní členové – po návratu k záložce je obnovíme.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoadedAt.current > 30_000) {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const toggleExpanded = useCallback((eventId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(eventId)) next.add(eventId);
      return next;
    });
  }, []);

  /** Optimistická změna – při chybě se vrátí zpět a zobrazí se hláška. */
  const applyTransport = useCallback((eventId: string, userId: string, transport: boolean) => {
    setData((current) => patchTransport(current, eventId, userId, transport));
    setPending((current) => new Set(current).add(pendingKey(eventId, userId)));
    setNotice(null);

    void setTransport(eventId, userId, transport)
      .catch((updateError: Error) => {
        setData((current) => patchTransport(current, eventId, userId, !transport));
        setNotice(updateError.message);
      })
      .finally(() => {
        setPending((current) => {
          const next = new Set(current);
          next.delete(pendingKey(eventId, userId));
          return next;
        });
      });
  }, []);

  const pendingByEvent = useCallback(
    (eventId: string) => {
      const prefix = `${eventId}:`;
      const users = new Set<string>();
      for (const key of pending) {
        if (key.startsWith(prefix)) users.add(key.slice(prefix.length));
      }
      return users;
    },
    [pending],
  );

  return (
    <div className="app">
      <header className="header">
        <div className="header__bar">
          <div className="header__identity">
            <ClubLogo />
            <div>
              <h1 className="header__title">Oddílová doprava</h1>
              {/* Klubový název nese logo, podtitulek proto jen vymezuje období. */}
              <p className="header__subtitle">
                {data ? `Závody do ${formatFullDate(data.range.to)}` : 'Sportcentrum Jičín'}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="button button--ghost"
            onClick={() => void load({ refresh: true })}
            disabled={refreshing}
          >
            <RefreshIcon spinning={refreshing} />
            <span>{refreshing ? 'Načítám…' : 'Obnovit'}</span>
          </button>
        </div>
      </header>

      <main className="main">
        {data?.stale && (
          <p className="banner banner--warn">ORIS je právě nedostupný, zobrazuji poslední načtená data.</p>
        )}
        {notice && (
          <p className="banner banner--error" role="alert">
            {notice}
          </p>
        )}

        {status === 'loading' && <SkeletonList />}

        {status === 'error' && (
          <div className="empty">
            <p className="empty__title">Data se nepodařilo načíst</p>
            <p className="empty__text">{error}</p>
            <button type="button" className="button" onClick={() => void load({ refresh: true })}>
              Zkusit znovu
            </button>
          </div>
        )}

        {status === 'ready' && data && data.events.length === 0 && (
          <div className="empty">
            <p className="empty__title">Žádné závody</p>
            <p className="empty__text">
              V období do {formatFullDate(data.range.to)} není na žádný závod přihlášen nikdo z oddílu.
            </p>
          </div>
        )}

        {status === 'ready' && data && data.events.length > 0 && (
          <div className="list">
            {data.events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                expanded={expanded.has(event.id)}
                pendingUserIds={pendingByEvent(event.id)}
                onToggleExpanded={toggleExpanded}
                onToggleTransport={applyTransport}
              />
            ))}
          </div>
        )}
      </main>

      {data && <footer className="footer">Data z ORISu, aktualizováno v {formatTime(data.fetchedAt)}.</footer>}
    </div>
  );
}

/** Vrátí novou kopii dat se změněnou přihláškou a přepočteným počtem na dopravu. */
function patchTransport(
  data: EventsResponse | null,
  eventId: string,
  userId: string,
  transport: boolean,
): EventsResponse | null {
  if (!data) return data;

  return {
    ...data,
    events: data.events.map((event) => {
      if (event.id !== eventId) return event;

      const entries = event.entries.map((entry) =>
        entry.userId === userId ? { ...entry, transport } : entry,
      );
      return {
        ...event,
        entries,
        transportCount: entries.filter((entry) => entry.transport).length,
      };
    }),
  };
}

/** Oddílové logo z `client/public/logo.png`; když soubor chybí, zůstane textová značka. */
function ClubLogo() {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="header__logo header__logo--fallback" aria-hidden="true">
        SJC
      </span>
    );
  }

  return (
    <img
      className="header__logo"
      src="/logo.png"
      alt="Sportcentrum Jičín"
      width={44}
      height={44}
      onError={() => setFailed(true)}
    />
  );
}

function SkeletonList() {
  return (
    <div className="list" aria-hidden="true">
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="skeleton" />
      ))}
    </div>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      className={spinning ? 'spin' : undefined}
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M20 11a8 8 0 1 0-.7 4.3" strokeLinecap="round" />
      <path d="M20 5v6h-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
